// Shared benchmark helpers — one definition used by the Benchmarks page, the
// Markten per-market table and the BenchmarkRow component.
import type { Benchmark } from "@/lib/api/types";

/** Additive "flow" metrics accumulate over the window, so a monthly target must
 *  be scaled to the selected window length. Rates and averages are period-agnostic.
 *  Reach is not in the list: unique people do not grow in proportion to the
 *  number of days (60 days is not twice the reach of 30), so a reach target is
 *  compared as set. */
export const ADDITIVE_METRICS = new Set<string>([
  "spend", "revenue", "orders", "clicks", "impressions",
  "applications", "content_pieces", "video_views", "follower_growth",
  "new_customers", "branded_search", "website_visitors",
]);

/** Scale a (possibly monthly) target to the selected window for additive metrics. */
export function scaleTarget(target: number, metricKey: string, period: string, days: number): number {
  return period === "monthly" && ADDITIVE_METRICS.has(metricKey) ? target * (days / 30) : target;
}

/** Meeting/beating the target in the right direction? null = neutral or unknown. */
export function onTrack(actual: number | null, target: number | null, higherIsBetter: boolean | null): boolean | null {
  if (actual == null || target == null || higherIsBetter == null) return null;
  return higherIsBetter === false ? actual <= target : actual >= target;
}

/** Resolve the RAW (unscaled) target for a metric, preferring a region-specific
 *  benchmark, then the global (null region + null brand) one. */
export function benchmarkTarget(benchmarks: Benchmark[], metricKey: string, region: string | null): number | null {
  const match =
    (region ? benchmarks.find((b) => b.metric_key === metricKey && b.region === region) : undefined) ??
    benchmarks.find((b) => b.metric_key === metricKey && !b.region && !b.brand_id);
  return match ? match.target_value : null;
}
